import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  CalendarDays,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Copy,
Upload,
  Plus,
  Printer,
  Trash2,
  X,
} from 'lucide-react';
import { breakKindOf } from '@/components/timetable/breakKind';

import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Tabs, tabPanelId } from '@/components/ui/Tabs';
import { holidaysApi } from '@/features/institute/holidays/api/holidays.api';
import { holidayOn } from '@/features/institute/holidays/utils';
import { allocationsApi } from '@/features/institute/staff/api/allocations.api';
import { ClassTeachingGrid } from '@/features/institute/staff/components/ClassTeachingGrid';
import { examsApi } from '@/features/teacher/api/exams.api';
import { periodTitle, WEEKDAY_SHORT } from '@/features/teacher/components/timetable/timetableCopy';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords, formatDate, formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Exam } from '@/types/exam';
import type { TimetablePeriod, Weekday } from '@/types/timetable';
import type { AllottableSubject, SectionWeekEntry, TimetableVersion } from '@/types/timetableAdmin';
import { instituteTimetableApi } from '../api/timetable.api';
import { CopyTimetableModal } from '../components/CopyTimetableModal';
import { CreatePeriodModal } from '../components/CreatePeriodModal';
import { DeleteTimetableModal } from '../components/DeleteTimetableModal';
import { EditLessonModal } from '../components/EditLessonModal';
import { ExamPaperModal } from '../components/ExamPaperModal';
import { ExamTimetableTable } from '../components/ExamTimetableTable';
import { PeriodTimingModal } from '../components/PeriodTimingModal';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { PlaceLessonModal } from '../components/PlaceLessonModal';
import { TimetableCalendar } from '../components/TimetableCalendar';
import { TimetableDatesModal } from '../components/TimetableDatesModal';
import { TimetableLegend } from '../components/TimetableLegend';
import { TimetableRangeBar } from '../components/TimetableRangeBar';
import { TimetableSummaryCards } from '../components/TimetableSummaryCards';
import { TimetableWeekGrid, type WeekColumn } from '../components/TimetableWeekGrid';
import { exportExamsCsv, exportWeekCsv } from '../exportTimetable';
import {
  WEEKDAY_ORDER,
  durationMinutes,
  startOfToday,
  toDayString,
  examHoldsPeriod,
  weekInfoFor,
  weekOffsetOf,
  REPEATED_SUBJECT_WARNING,
  repeatsSubjectOnDay,
} from '../timetableWeek';
import { describeTimetableRange, timetableForWeek } from '../timetableRange';

/** Both filters offer this, and it is what "no filter" is called on the wire. */
const ALL = 'ALL';

/** The two things this screen can draw for a section. */
const TIMETABLE_TYPE_OPTIONS = [
  { value: 'REGULAR', label: 'Regular classes' },
  { value: 'EXAM', label: 'Exam timetable' },
] as const;

/**
 * The academic office's timetable: the recurring week for one section, and the
 * examination timetable that sits over it.
 *
 * Everything on this screen is read from the institute's own data. It used to
 * carry a demonstration timetable underneath — eight invented bell times, five
 * Mathematics lessons taught by a teacher who does not exist, six exam papers
 * with marks already entered — shown whenever a real query came back empty. A
 * new institute therefore opened this page on somebody else's week, could not
 * edit any of it, and had no way to tell which rows were real. The empty state
 * is the honest answer, and it says what to do next.
 *
 * The week is *recurring*: an entry says 8-A has Mathematics in period 3 on
 * Mondays and repeats for as long as its timetable applies. A section can have
 * several timetables between different dates, so the week picker also decides
 * which one is on screen — always the one in force that week. It adds the
 * calendar too: the dates on the column headers, and the closures the office
 * has declared against them.
 */
const EXAM_LIST_TABS = ['scheduled', 'past', 'deleted'] as const;
type ExamListTab = (typeof EXAM_LIST_TABS)[number];

const TIMETABLE_TYPES = ['REGULAR', 'EXAM'] as const;

/**
 * What the office is looking at, as it sits in the address bar, so a refresh
 * or coming back finds the same class, section, week and filters.
 *
 * The week is stored as its Monday (`YYYY-MM-DD`) rather than as an offset from
 * today: an offset saved on Friday would point at a different week by Monday.
 * Blank is this week.
 */
type Filters = {
  class: string;
  section: string;
  week: string;
  type: (typeof TIMETABLE_TYPES)[number];
  subject: string;
  teacher: string;
  exams: ExamListTab;
};

const DEFAULT_FILTERS: Filters = {
  class: '',
  section: '',
  week: '',
  type: 'REGULAR',
  subject: ALL,
  teacher: ALL,
  exams: 'scheduled',
};

/** Class, section, subject and teacher are the school's own uuids; an unknown one falls back below. */
const FILTER_OPTIONS = { allowed: { type: TIMETABLE_TYPES, exams: EXAM_LIST_TABS } };

/** A stored Monday back to a week offset from today; anything malformed is this week. */
function offsetOfStoredWeek(week: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(week);
  if (!match) return 0;
  return weekOffsetOf(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

export function InstituteTimetablePage() {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const {
    class: selectedClassUuid,
    section: sectionUuid,
    type: timetableType,
    subject: filterSubject,
    teacher: filterTeacher,
    exams: examList,
  } = filters;
  const weekOffset = offsetOfStoredWeek(filters.week);
  const setWeekOffset = (offset: number) =>
    setFilters({ week: offset === 0 ? '' : weekInfoFor(offset).dates[0] });
  const setTimetableType = (type: Filters['type']) => setFilters({ type });
  const setFilterSubject = (subject: string) => setFilters({ subject });
  const setFilterTeacher = (teacher: string) => setFilters({ teacher });
  const setExamList = (exams: ExamListTab) => setFilters({ exams });

  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = startOfToday();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarToggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isCalendarOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        calendarToggleRef.current &&
        !calendarToggleRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  // Building the week
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddPeriodOpen, setIsAddPeriodOpen] = useState(false);
  const [placingSlot, setPlacingSlot] = useState<{ day: Weekday; period: TimetablePeriod } | null>(
    null,
  );
  const [editingEntry, setEditingEntry] = useState<SectionWeekEntry | null>(null);
  const [removingEntry, setRemovingEntry] = useState<SectionWeekEntry | null>(null);
  const [isRemovingEntry, setIsRemovingEntry] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<TimetablePeriod | null>(null);
  const [removingPeriod, setRemovingPeriod] = useState<TimetablePeriod | null>(null);
  const [isRemovingPeriod, setIsRemovingPeriod] = useState(false);
  /** The dates dialog: starting a timetable, or moving the one given. */
  const [datesDialog, setDatesDialog] = useState<{ timetable?: TimetableVersion } | null>(null);
  const [removingTimetable, setRemovingTimetable] = useState<TimetableVersion | null>(null);
  const [isRemovingTimetable, setIsRemovingTimetable] = useState(false);
  /** The draft paper whose Publish is in flight. */
  const [publishingExam, setPublishingExam] = useState<string | null>(null);
  /** The draft whose Publish was chosen, held while the office confirms it. */
  const [publishingTimetable, setPublishingTimetable] = useState<TimetableVersion | null>(null);
  const [isPublishingTimetable, setIsPublishingTimetable] = useState(false);

  // Exams
  const [isScheduleExamOpen, setIsScheduleExamOpen] = useState(false);
  const [editingExam, setEditingExam] = useState<Exam | null>(null);
  const [removingExam, setRemovingExam] = useState<Exam | null>(null);
  const [isRemovingExam, setIsRemovingExam] = useState(false);
  const [restoringExam, setRestoringExam] = useState<Exam | null>(null);
  const [isRestoringExam, setIsRestoringExam] = useState(false);

  // Dragging a lesson to another cell
  const [moving, setMoving] = useState<SectionWeekEntry | null>(null);
  /** The exam date a lesson was refused on, while its notice is open. */
  const [examDayNotice, setExamDayNotice] = useState<string | null>(null);
  const [isSavingMove, setIsSavingMove] = useState(false);
  /** A move held for confirmation: the subject is already on the day it lands on. */
  const [repeatMove, setRepeatMove] = useState<{
    entryUuid: string;
    day: Weekday;
    period: TimetablePeriod;
  } | null>(null);

  const isExamView = timetableType === 'EXAM';
  // Placing a lesson in a subject nobody teaches here also allots its teacher,
  // which is the allotment screen's permission, not the timetable's.
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canAllotTeachers = Boolean(permissions?.includes('ALLOCATION_MANAGE'));
  const canManageTimetable = Boolean(permissions?.includes('TIMETABLE_MANAGE'));

  // --- what the screen reads -------------------------------------------------

  const {
    data: options,
    isLoading: isLoadingOptions,
    error: optionsError,
    refetch: refetchOptions,
  } = useApiQuery(() => allocationsApi.options(), []);

  const { data: periods, refetch: refetchPeriods } = useApiQuery(
    () => instituteTimetableApi.periods(),
    [],
  );

  /** The section's dated timetables — which one the grid shows depends on the week. */
  const {
    data: timetableList,
    isLoading: isLoadingTimetables,
    error: timetablesError,
    refetch: refetchTimetables,
  } = useApiQuery(
    () => (sectionUuid ? instituteTimetableApi.timetables(sectionUuid) : Promise.resolve(null)),
    [sectionUuid],
  );

  const {
    data: exams,
    isLoading: isLoadingExams,
    error: examsError,
    refetch: refetchExams,
  } = useApiQuery(
    () => (sectionUuid ? examsApi.list({ classSectionUuid: sectionUuid }) : Promise.resolve([])),
    [sectionUuid],
  );

  /**
   * Papers deleted from this section. Deleting only hides a paper — its marks
   * are kept — so the office can see what went and put it back.
   */
  const {
    data: deletedExams,
    isLoading: isLoadingDeletedExams,
    error: deletedExamsError,
    refetch: refetchDeletedExams,
  } = useApiQuery(
    () =>
      sectionUuid
        ? examsApi.list({ classSectionUuid: sectionUuid, status: 'suspended' })
        : Promise.resolve([]),
    [sectionUuid],
  );

  /**
   * The whole session's declared calendar, in one request rather than one per
   * month: an institute year holds a few dozen holidays, and the week picker
   * moves through months faster than a request per month could keep up with.
   */
  const { data: holidays } = useApiQuery(() => holidaysApi.list(), []);

  // --- the class and section on show ----------------------------------------

  const classes = useMemo(() => options?.classes ?? [], [options]);

  const selectedClass = useMemo(
    () => classes.find((item) => item.uuid === selectedClassUuid) ?? null,
    [classes, selectedClassUuid],
  );

  // Opens on the first class and its first section; nothing else picks for the
  // reader, so a section they chose survives a refetch. A class restored from
  // the address that no longer exists falls back the same way.
  useEffect(() => {
    if (classes.length === 0) return;
    if (selectedClassUuid && classes.some((item) => item.uuid === selectedClassUuid)) return;

    const firstClass = classes[0];
    setFilters({ class: firstClass.uuid, section: firstClass.sections[0]?.uuid ?? '' });
  }, [classes, selectedClassUuid, setFilters]);

  function handleClassChange(uuid: string) {
    const target = classes.find((item) => item.uuid === uuid);
    setFilters({ class: uuid, section: target?.sections[0]?.uuid ?? '' });
    resetForNewSection();
  }

  function handleSectionChange(uuid: string) {
    setFilters({ section: uuid });
    resetForNewSection();
  }

  /**
   * A filter set on one section means nothing on the next — its subjects and
   * its teachers are different — and leaving it on would show the new section
   * as an empty week for no reason the reader can see.
   */
  function resetForNewSection() {
    setMoving(null);
    setFilters({ exams: 'scheduled', subject: ALL, teacher: ALL });
  }

  // --- the week on show ------------------------------------------------------

  const weekInfo = useMemo(() => weekInfoFor(weekOffset), [weekOffset]);
  const today = toDayString(startOfToday());

  /** Only once the list belongs to the section on show — never the last section's. */
  const sectionTimetables = timetableList?.section.uuid === sectionUuid ? timetableList : null;

  /** The timetable in force in the week on screen, or null when none covers it. */
  const shownTimetable = useMemo(
    () =>
      sectionTimetables ? timetableForWeek(sectionTimetables.timetables, weekInfo.dates) : null,
    [sectionTimetables, weekInfo],
  );

  /**
   * The week, read from the timetable on show. Fetched even when no timetable
   * covers the week: the bell times, the subjects that may be placed and the
   * exam view all come from it — only its lessons are set aside then.
   */
  const {
    data: week,
    isLoading: isLoadingWeek,
    error: weekError,
    refetch: refetchWeek,
  } = useApiQuery(
    () =>
      sectionUuid && sectionTimetables
        ? instituteTimetableApi.sectionWeek(sectionUuid, shownTimetable?.uuid)
        : Promise.resolve(null),
    [sectionUuid, sectionTimetables, shownTimetable?.uuid],
  );

  /** A change to the lessons changes the counts the timetable bar shows too. */
  function refreshLessons() {
    refetchWeek();
    refetchTimetables();
  }

  // The picker follows the grid, so paging through weeks keeps the two agreeing.
  useEffect(() => {
    setCalendarMonth(new Date(weekInfo.monday.getFullYear(), weekInfo.monday.getMonth(), 1));
  }, [weekInfo.monday]);

  // Escape puts down a lesson being carried.
  useEffect(() => {
    if (!moving) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMoving(null);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [moving]);

  const workingDays = week?.workingDays ?? [];
  const activePeriods = week?.periods ?? periods ?? [];
  // A week no timetable covers has no lessons, whatever the server's default
  // timetable for the section holds.
  const entries = useMemo(
    () => (shownTimetable && week?.timetable?.uuid === shownTimetable.uuid ? week.entries : []),
    [week, shownTimetable],
  );

  /**
   * The papers that keep lessons out of the week: the institute's own, drafts
   * included — a paper the office has scheduled owns its periods whether or not
   * the class has been told yet. A class test is sat in its own subject's
   * period, so it holds nothing back. Read from all of the section's papers,
   * not the filtered list: a subject filter must not make an exam period look free.
   */
  const instituteExams = useMemo(
    () => (exams ?? []).filter((exam) => exam.origin === 'INSTITUTE'),
    [exams],
  );

  const columns = useMemo<WeekColumn[]>(
    () =>
      workingDays.map((day) => {
        const date = weekInfo.dates[WEEKDAY_ORDER.indexOf(day)];
        const parsed = new Date(`${date}T00:00:00`);
        const examHours = instituteExams.filter((exam) => exam.examDate === date);

        return {
          day,
          date,
          dayNumber: parsed.getDate(),
          monthShort: parsed.toLocaleDateString('en-IN', { month: 'short' }),
          isToday: date === today,
          holiday: holidayOn(holidays, date),
          hasExam: examHours.length > 0,
          examHours,
        };
      }),
    [workingDays, weekInfo, today, holidays, instituteExams],
  );

  /**
   * A regular lesson cannot be placed in a period an institute paper takes. The
   * exam runs then instead, so the office is shown it rather than a lesson
   * nobody will teach; the periods around the paper stay open. Only this
   * section's papers count — an exam in 9-A leaves 9-B's week open.
   */
  function examDateHolding(day: Weekday, period: TimetablePeriod) {
    const column = columns.find((candidate) => candidate.day === day);
    return column?.examHours.some((exam) => examHoldsPeriod(exam, period)) ? column.date : null;
  }

  function handlePlace(day: Weekday, period: TimetablePeriod) {
    const examDate = examDateHolding(day, period);
    if (examDate) {
      setExamDayNotice(examDate);
      return;
    }
    setPlacingSlot({ day, period });
  }

  /** The papers the notice lists, earliest first. */
  const noticeExams = useMemo(
    () =>
      instituteExams
        .filter((exam) => exam.examDate === examDayNotice)
        .sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? '')),
    [instituteExams, examDayNotice],
  );

  function viewExamTimetable() {
    setExamDayNotice(null);
    setMoving(null);
    setTimetableType('EXAM');
  }

  // --- filters ---------------------------------------------------------------

  /**
   * Who teaches each subject in this section, from the allotment — the same
   * answer the grid's cards carry, so filtering by a teacher and reading the
   * grid can never disagree.
   */
  const teacherOfSubject = useMemo(() => {
    const map = new Map<string, string>();
    for (const subject of week?.allottableSubjects ?? []) {
      if (subject.teacher) map.set(subject.uuid, subject.teacher);
    }
    return map;
  }, [week]);

  const subjectOptions = useMemo(() => {
    const names = new Set<string>();
    for (const subject of week?.allottableSubjects ?? []) names.add(subject.name);
    for (const entry of entries) names.add(entry.subject.name);
    for (const exam of exams ?? []) names.add(exam.subject.name);

    return [...names].sort((a, b) => a.localeCompare(b));
  }, [week, entries, exams]);

  const teacherOptions = useMemo(() => {
    const names = new Set<string>();
    for (const subject of week?.allottableSubjects ?? []) {
      if (subject.teacher) names.add(subject.teacher);
    }
    for (const entry of entries) {
      if (entry.teacher) names.add(entry.teacher);
    }

    return [...names].sort((a, b) => a.localeCompare(b));
  }, [week, entries]);

  const isFiltered = filterSubject !== ALL || filterTeacher !== ALL;

  function matchesFilter(entry: SectionWeekEntry) {
    if (filterSubject !== ALL && entry.subject.name !== filterSubject) return false;
    if (filterTeacher !== ALL && entry.teacher !== filterTeacher) return false;
    return true;
  }

  const visibleEntryCount = useMemo(
    () => entries.filter(matchesFilter).length,
    // `matchesFilter` closes over both filters; they are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, filterSubject, filterTeacher],
  );

  // --- the exam timetable ----------------------------------------------------

  /** The subject and teacher filters, then date order — for both exam lists. */
  const arrangeExams = useCallback(
    (list: Exam[]) =>
      list
      .filter((exam) => {
        if (filterSubject !== ALL && exam.subject.name !== filterSubject) return false;
        if (
          filterTeacher !== ALL &&
          teacherOfSubject.get(exam.subject.uuid) !== filterTeacher
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const byDate = a.examDate.localeCompare(b.examDate);
        if (byDate !== 0) return byDate;
        // Within the same date, morning papers come before afternoon ones.
        const sessionOrder = (s: string | null) => (s === 'MORNING' ? 0 : 1);
        const bySession = sessionOrder(a.session) - sessionOrder(b.session);
        if (bySession !== 0) return bySession;
        return a.name.localeCompare(b.name);
      }),
    [filterSubject, filterTeacher, teacherOfSubject],
  );

  const sectionExams = useMemo(() => arrangeExams(exams ?? []), [arrangeExams, exams]);
  const sectionDeletedExams = useMemo(
    () => arrangeExams(deletedExams ?? []),
    [arrangeExams, deletedExams],
  );

  /**
   * Papers falling in the week on show.
   *
   * The week picker sits above both views, and in the exam view it had no
   * effect at all — every paper was listed whichever week was chosen. Papers
   * outside the week are not hidden without saying so: the empty state below
   * counts them and offers to jump to the first.
   */
  const weekExams = useMemo(
    () =>
      sectionExams.filter(
        (exam) => exam.examDate >= weekInfo.dates[0] && exam.examDate <= weekInfo.dates[6],
      ),
    [sectionExams, weekInfo],
  );

  /**
   * Every paper sat before today, newest first, whatever week is on show — the
   * week view keeps the timetable short, and this is where the finished papers
   * can be read back without paging through the weeks they fell in.
   */
  const pastExams = useMemo(
    () => sectionExams.filter((exam) => exam.examDate < today).reverse(),
    [sectionExams, today],
  );

  const nextExamElsewhere = useMemo(() => {
    const upcoming = sectionExams.find((exam) => exam.examDate > weekInfo.dates[6]);
    return upcoming ?? [...sectionExams].reverse().find((exam) => exam.examDate < weekInfo.dates[0]) ?? null;
  }, [sectionExams, weekInfo]);

  // --- the five numbers ------------------------------------------------------

  const summary = useMemo(() => {
    const teaching = activePeriods.filter((period) => period.kind === 'TEACHING');
    const breaks = activePeriods.filter((period) => period.kind === 'BREAK');
    // The institute's own wording decides what each break is (`breakKindOf`).
    // Assembly is a break on the grid but neither a lunch nor a recess, so it
    // is counted under neither card.
    const lunch = breaks.filter((period) => breakKindOf(period.label) === 'LUNCH');
    const short = breaks.filter((period) => breakKindOf(period.label) === 'SHORT');
    const minutes = (rows: TimetablePeriod[]) =>
      rows.reduce((total, row) => total + durationMinutes(row.startTime, row.endTime), 0);

    return {
      periodsPerDay: teaching.length,
      totalPeriods: teaching.length * workingDays.length,
      workingDaysCount: workingDays.length,
      subjectCount: new Set(entries.map((entry) => entry.subject.name)).size,
      shortBreakCount: short.length,
      shortBreakMinutes: minutes(short),
      lunchBreakCount: lunch.length,
      lunchBreakMinutes: minutes(lunch),
    };
  }, [activePeriods, workingDays, entries]);

  // --- writes ----------------------------------------------------------------

  function describeFailure(caught: unknown, fallback: string) {
    return caught instanceof ApiRequestError ? caught.message : fallback;
  }

  async function confirmRemoveEntry() {
    if (!removingEntry) return;

    setIsRemovingEntry(true);
    try {
      await instituteTimetableApi.removeEntry(removingEntry.uuid);
      toast.success(
        `${capitalizeWords(removingEntry.subject.name)} is deleted`,
        'Taken out of this weekly timetable.',
      );
      setRemovingEntry(null);
      refreshLessons();
    } catch (caught) {
      toast.error(
        'Could not delete that lesson',
        describeFailure(caught, 'Something went wrong. Please try again.'),
      );
    } finally {
      setIsRemovingEntry(false);
    }
  }

  async function confirmRemovePeriod() {
    if (!removingPeriod) return;

    setIsRemovingPeriod(true);
    try {
      await instituteTimetableApi.removePeriod(removingPeriod.uuid);
      toast.success(`${periodTitle(removingPeriod)} is removed`);
      setRemovingPeriod(null);
      refetchPeriods();
      refetchWeek();
    } catch (caught) {
      toast.error(
        'Could not remove that period',
        describeFailure(caught, 'Something went wrong. Please try again.'),
      );
    } finally {
      setIsRemovingPeriod(false);
    }
  }

  async function confirmRemoveTimetable() {
    if (!removingTimetable) return;

    setIsRemovingTimetable(true);
    try {
      await instituteTimetableApi.removeTimetable(removingTimetable.uuid);
      toast.success(
        'Timetable deleted',
        `${describeTimetableRange(removingTimetable)} is gone, with its lessons.`,
      );
      setRemovingTimetable(null);
      refreshLessons();
    } catch (caught) {
      toast.error(
        'Could not delete that timetable',
        describeFailure(caught, 'Something went wrong. Please try again.'),
      );
    } finally {
      setIsRemovingTimetable(false);
    }
  }

  async function confirmPublishTimetable() {
    if (!publishingTimetable) return;

    setIsPublishingTimetable(true);
    try {
      await instituteTimetableApi.publishTimetable(publishingTimetable.uuid);
      toast.success(
        'Timetable published',
        `${describeTimetableRange(publishingTimetable)} — its teachers and families can see it now.`,
      );
      setPublishingTimetable(null);
      refreshLessons();
    } catch (caught) {
      toast.error(
        'Could not publish that timetable',
        describeFailure(caught, 'Something went wrong. Please try again.'),
      );
    } finally {
      setIsPublishingTimetable(false);
    }
  }

  /** Releases a draft paper as it stands, straight from its row. */
  async function publishExam(exam: Exam) {
    setPublishingExam(exam.uuid);
    try {
      const saved = await examsApi.update(exam.uuid, { publication: 'PUBLISHED' });
      toast.success(
        `${saved.name} is scheduled`,
        'Students, parents and the section\'s teachers can see it now.',
      );
      refetchExams();
    } catch (caught) {
      toast.error(
        `Could not publish ${exam.name}`,
        describeFailure(caught, 'Something went wrong. Please try again.'),
      );
    } finally {
      setPublishingExam(null);
    }
  }

  async function confirmRemoveExam() {
    if (!removingExam) return;

    setIsRemovingExam(true);
    try {
      await examsApi.remove(removingExam.uuid);
      toast.success(`${removingExam.name} is deleted`, 'Taken off the examination timetable.');
      setRemovingExam(null);
      refetchExams();
      refetchDeletedExams();
    } catch (caught) {
      toast.error(
        'Could not remove that exam',
        describeFailure(caught, 'Something went wrong. Please try again.'),
      );
    } finally {
      // Its own flag: this used to clear the *period* dialog's, leaving the
      // exam dialog stuck on "Removing…" forever once anything went wrong.
      setIsRemovingExam(false);
    }
  }

  async function confirmRestoreExam() {
    if (!restoringExam) return;

    setIsRestoringExam(true);
    try {
      const restored = await examsApi.restore(restoringExam.uuid);
      toast.success(
        `${restored.name} is restored`,
        `Back on the examination timetable for ${formatDate(restored.examDate)}.`,
      );
      setRestoringExam(null);
      refetchExams();
      refetchDeletedExams();
    } catch (caught) {
      toast.error(
        'Could not restore that exam',
        describeFailure(caught, 'Something went wrong. Please try again.'),
      );
    } finally {
      setIsRestoringExam(false);
    }
  }

  async function moveTo(day: Weekday, period: TimetablePeriod, confirmedRepeat = false) {
    if (!moving || isSavingMove) return;

    const entry = moving;
    if (entry.dayOfWeek === day && entry.period.uuid === period.uuid) {
      setMoving(null);
      return;
    }

    // Moving a lesson into an exam period creates one there, so the same rule as placing.
    const examDate = examDateHolding(day, period);
    if (examDate) {
      setExamDayNotice(examDate);
      return;
    }

    // Within the same day the subject is not added to it, only shifted.
    if (
      !confirmedRepeat &&
      entry.dayOfWeek !== day &&
      repeatsSubjectOnDay(entries, entry.subject.uuid, day, entry.uuid)
    ) {
      setRepeatMove({ entryUuid: entry.uuid, day, period });
      return;
    }

    setRepeatMove(null);
    setIsSavingMove(true);
    try {
      // No subject and no room: a drag says when a lesson runs, nothing else.
      await instituteTimetableApi.updateEntry(entry.uuid, {
        periodUuid: period.uuid,
        dayOfWeek: day,
      });
      toast.success(
        `${capitalizeWords(entry.subject.name)} moved`,
        `${WEEKDAY_SHORT[day]}, ${periodTitle(period)}.`,
      );
      setMoving(null);
      refetchWeek();
    } catch (caught) {
      toast.error(
        'Could not move that lesson',
        describeFailure(caught, 'Something went wrong. Please try again.'),
      );
    } finally {
      setIsSavingMove(false);
    }
  }

  function handleExport() {
    const sectionName = week?.section.displayName ?? selectedClass?.name ?? 'Timetable';

    if (isExamView) {
      if (weekExams.length === 0) {
        toast.info('Nothing to export', 'No papers are scheduled in the week on screen.');
        return;
      }
      exportExamsCsv(sectionName, weekExams);
      toast.success('Exam timetable exported', `${weekExams.length} papers written to a CSV file.`);
      return;
    }

    if (!week || activePeriods.length === 0) {
      toast.info('Nothing to export', 'Set up the periods of the day first.');
      return;
    }

    exportWeekCsv({
      sectionName,
      rangeLabel: weekInfo.rangeLabel,
      workingDays,
      periods: activePeriods,
      entries: entries.filter(matchesFilter),
    });
    toast.success('Timetable exported', `${sectionName}, ${weekInfo.rangeLabel}.`);
  }

  function jumpToWeekOf(date: Date) {
    setWeekOffset(weekOffsetOf(date));
    setCalendarMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    setIsCalendarOpen(false);
  }

  // --- rendering -------------------------------------------------------------

  const sectionOptions = selectedClass?.sections ?? [];
  const selectedSection = sectionOptions.find((item) => item.uuid === sectionUuid) ?? null;
  /**
   * What the section on show teaches: its class's subjects, narrowed to the
   * section's own list where it has one (`subjectUuids` null = all of them) —
   * the server refuses a lesson in any other, so the picker does not offer it.
   */
  const chosenForSection = selectedSection?.subjectUuids;
  const sectionSubjects = (selectedClass?.subjects ?? []).filter(
    (subject) => !chosenForSection || chosenForSection.includes(subject.uuid),
  );

  /**
   * Subjects available to schedule an exam paper for.
   * If a teacher is allotted, their name is displayed alongside the subject.
   * If not yet allotted, the subject name is still shown so the office can schedule the exam paper.
   */
  const examSubjects: AllottableSubject[] = useMemo(() => {
    const teacherBySubject = new Map<string, string>();
    for (const item of week?.allottableSubjects ?? []) {
      if (item.teacher) {
        teacherBySubject.set(item.uuid, item.teacher);
      }
    }

    const availableSubjects =
      sectionSubjects.length > 0 ? sectionSubjects : (selectedClass?.subjects ?? []);

    const list: AllottableSubject[] = [];
    const seen = new Set<string>();

    for (const sub of availableSubjects) {
      seen.add(sub.uuid);
      list.push({
        uuid: sub.uuid,
        name: sub.name,
        teacher: teacherBySubject.get(sub.uuid) ?? null,
      });
    }

    for (const item of week?.allottableSubjects ?? []) {
      if (!seen.has(item.uuid)) {
        seen.add(item.uuid);
        list.push({
          uuid: item.uuid,
          name: item.name,
          teacher: item.teacher ?? null,
        });
      }
    }

    return list;
  }, [sectionSubjects, selectedClass?.subjects, week?.allottableSubjects]);

  const hasClasses = classes.length > 0;
  const hasSection = Boolean(sectionUuid);

  return (
    <div className="flex flex-col gap-6 text-slate-900 dark:text-slate-100 print:w-full print:max-w-none print:block print:gap-3 print:text-black print:bg-white">
      {/* Print-only Header Banner */}
      <div className="hidden print:block mb-3 pb-2 border-b border-slate-300 w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black text-slate-900">
              {selectedClass?.name ?? ''} {week?.section.displayName ? `(${week.section.displayName})` : ''} - {isExamView ? 'Examination Timetable' : 'Weekly Timetable'}
            </h1>
            <p className="text-xs text-slate-600 font-medium mt-0.5">
              Week of {weekInfo.rangeLabel}
            </p>
          </div>
          <div className="text-right text-xs text-slate-600 font-medium">
            Printed on {formatDate(today)}
          </div>
        </div>
      </div>

      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
        <div className="flex items-center gap-3.5">
          
          <div>
            <h1 className="text-2xl  font-black text-slate-900 dark:text-white tracking-tight">
              Timetable
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
              Build the weekly timetable for each class, and schedule examination papers.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full md:w-auto">
          <button
            type="button"
            onClick={handleExport}
            title="Save what is on screen as a CSV file"
            className="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white px-3 sm:px-4 py-2 sm:py-2.5 border border-slate-200 dark:border-slate-800 text-xs sm:text-sm font-bold inline-flex items-center gap-1.5 sm:gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <Upload className="h-4 w-4 text-slate-500 shrink-0" />
            <span>Export CSV</span>
          </button>

          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white px-3 sm:px-4 py-2 sm:py-2.5 border border-slate-200 dark:border-slate-800 text-xs sm:text-sm font-bold inline-flex items-center gap-1.5 sm:gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <Printer className="h-4 w-4 text-slate-500 shrink-0" />
            <span>Print</span>
          </button>

          <button
            type="button"
            onClick={() => setIsCopyModalOpen(true)}
            className="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white px-3 sm:px-4 py-2 sm:py-2.5 border border-slate-200 dark:border-slate-800 text-xs sm:text-sm font-bold inline-flex items-center gap-1.5 sm:gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <Copy className="h-4 w-4 text-slate-500 shrink-0" />
            <span>Copy Timetable</span>
          </button>

          <button
            type="button"
            onClick={() => setIsDeleteModalOpen(true)}
            className="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400 hover:text-rose-700 px-3 sm:px-4 py-2 sm:py-2.5 border border-slate-200 dark:border-slate-800 text-xs sm:text-sm font-bold inline-flex items-center gap-1.5 sm:gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
          >
            <Trash2 className="h-4 w-4 text-rose-500 shrink-0" />
            <span>Delete Timetable</span>
          </button>

          <button
            type="button"
            onClick={() => setIsAddPeriodOpen(true)}
            className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-bold inline-flex items-center gap-1.5 sm:gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            <Plus className="h-4 w-4 shrink-0" />
            <span>Add a period</span>
          </button>
        </div>
      </header>

      <TimetableSummaryCards {...summary} />

      {optionsError && (
        <QueryError
          title="Could not load your classes"
          error={optionsError}
          onRetry={refetchOptions}
        />
      )}

      {/* --- filters -------------------------------------------------------- */}
      <div className="rounded-2xl p-2.5 bg-slate-50 dark:bg-[#0b1322] border border-slate-200/90 dark:border-slate-800 shadow-xs transition-all duration-300 print:hidden relative z-10">
        <div className="flex flex-col 2xl:flex-row 2xl:items-end gap-2.5 justify-between">
          {/* Every dropdown in this product is `components/ui/Select` — a native
              one is drawn by the operating system and ignores the theme. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2 min-w-0 2xl:flex-1">
            <Select
              label="Class"
              labelClassName="text-[12px] font-bold text-slate-700 dark:text-slate-200"
              options={classes.map((item) => ({ value: item.uuid, label: item.name }))}
              value={selectedClassUuid}
              onChange={(event) => handleClassChange(event.target.value)}
              disabled={!hasClasses}
              placeholder={hasClasses ? undefined : 'No classes yet'}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />

            <Select
              label="Section"
              labelClassName="text-[12px] font-bold text-slate-700 dark:text-slate-200"
              options={sectionOptions.map((item) => ({ value: item.uuid, label: item.name }))}
              value={sectionUuid}
              onChange={(event) => handleSectionChange(event.target.value)}
              disabled={sectionOptions.length === 0}
              placeholder={sectionOptions.length === 0 ? 'No sections' : undefined}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />

            <Select
              label="Timetable type"
              labelClassName="text-[12px] font-bold text-slate-700 dark:text-slate-200"
              options={TIMETABLE_TYPE_OPTIONS}
              value={timetableType}
              onChange={(event) => setTimetableType(event.target.value as Filters['type'])}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />

            <Select
              label="Subject"
              labelClassName="text-[12px] font-bold text-slate-700 dark:text-slate-200"
              options={[
                { value: ALL, label: 'All subjects' },
                ...subjectOptions.map((name) => ({ value: name, label: capitalizeWords(name) })),
              ]}
              value={filterSubject}
              onChange={(event) => setFilterSubject(event.target.value)}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />

            {/*
              The teacher field and the way out of both filters, in one cell.

              Beside the last dropdown rather than at the end of the bar: it is
              the control somebody's hand is already on when a filter empties
              the week, and a way back that sits half a screen away from that
              moment is one nobody finds.

              Only while a filter is actually set — nothing here offers to undo
              something that has not been done — and the field gives up the
              width for it only then. On a phone the label goes and the cross
              stands alone; `aria-label` carries the name either way.
            */}
            <Select
              label="Teacher"
              labelClassName="text-[12px] font-bold text-slate-700 dark:text-slate-200"
              options={[
                { value: ALL, label: 'All teachers' },
                ...teacherOptions.map((name) => ({ value: name, label: name })),
              ]}
              value={filterTeacher}
              onChange={(event) => setFilterTeacher(event.target.value)}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />
          </div>

          <div className="flex flex-wrap items-end gap-2 w-full 2xl:w-auto">
            {isFiltered && (
              <button
                type="button"
                onClick={() => {
                  setFilterSubject(ALL);
                  setFilterTeacher(ALL);
                }}
                aria-label="Clear filters"
                title="Clear the subject and teacher filters"
                className="!h-9 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] hover:bg-slate-50 hover:text-rose-600 hover:border-rose-300 text-slate-700 dark:text-slate-200 px-3 text-[13px] font-bold transition-all shadow-2xs cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 shrink-0"
              >
                <X className="h-3.5 w-3.5 text-rose-500" />
                <span>Clear filters</span>
              </button>
            )}

            <div className={cn("flex flex-col gap-1.5 relative w-full sm:w-auto", isCalendarOpen && "z-50")}>
              <span className="text-[12px] font-bold text-slate-700 dark:text-slate-200">{weekInfo.descriptor}</span>
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full sm:w-auto">
                <div className="flex !h-9 items-center bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-700/80 rounded-xl px-2 sm:px-2.5 justify-between gap-1 text-slate-700 dark:text-slate-200 shadow-2xs hover:border-indigo-300 transition-colors flex-1 sm:flex-initial min-w-0 max-w-full">
                  <button
                    type="button"
                    aria-label="Previous week"
                    title="Previous week"
                    className="p-1 hover:text-indigo-600 dark:hover:text-white text-slate-400 transition-colors cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    onClick={() => setWeekOffset(weekOffset - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <span className="font-bold text-slate-700 dark:text-slate-200 text-xs sm:text-[13px] truncate px-1 text-center min-w-[100px] sm:min-w-[120px]">
                    {weekInfo.rangeLabel}
                  </span>
                  <button
                    type="button"
                    aria-label="Next week"
                    title="Next week"
                    className="p-1 hover:text-indigo-600 dark:hover:text-white text-slate-400 transition-colors cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    onClick={() => setWeekOffset(weekOffset + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  <span className="h-4 w-px bg-slate-200 dark:bg-slate-700 mx-0.5" />
                  <button
                    ref={calendarToggleRef}
                    type="button"
                    aria-label="Pick a week from the calendar"
                    aria-expanded={isCalendarOpen}
                    title="Pick a week from the calendar"
                    className={cn(
                      'p-1 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                      isCalendarOpen && 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-slate-800',
                    )}
                    onClick={() => setIsCalendarOpen((open) => !open)}
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => jumpToWeekOf(startOfToday())}
                  disabled={weekInfo.isCurrent}
                  className="!h-9 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs sm:text-[13px] font-bold px-2.5 sm:px-3.5 transition-all cursor-pointer shadow-2xs shrink-0 active:scale-95 border border-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                >
                  This week
                </button>
              </div>

              {isCalendarOpen && (
                <TimetableCalendar
                  ref={calendarRef}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  weekDates={weekInfo.dates}
                  workingDays={workingDays.length > 0 ? workingDays : WEEKDAY_ORDER}
                  holidays={holidays}
                  onPickDate={jumpToWeekOf}
                />
              )}
            </div>
          </div>
        </div>

      </div>

      {/* --- the week, or the papers ---------------------------------------- */}
      <div className="w-full flex flex-col gap-5">
        {!isLoadingOptions && !hasClasses && !optionsError && (
          <Panel>
            <EmptyState
              icon={<CalendarDays className="h-8 w-8 text-indigo-600" />}
              title="No classes set up yet"
              description="A timetable is built against a class section. Add your classes and sections first, then come back here."
            />
          </Panel>
        )}

        {hasClasses && !hasSection && (
          <Panel>
            <EmptyState
              icon={<CalendarDays className="h-8 w-8 text-indigo-600" />}
              title={`${selectedClass?.name ?? 'This class'} has no sections`}
              description="Add a section to this class, then its weekly timetable can be built here."
            />
          </Panel>
        )}

        {/* The whole class, not just the section on show: whose register each
            section is and who takes each subject there, so a lesson placed
            below is never a guess at who will teach it. */}
      
        {hasSection && !isExamView && (
          <>
            {timetablesError && (
              <QueryError
                title="Could not load this section's timetables"
                error={timetablesError}
                onRetry={refetchTimetables}
              />
            )}

            {sectionTimetables && (
              <TimetableRangeBar
                timetables={sectionTimetables.timetables}
                shown={shownTimetable}
                canManage={canManageTimetable}
                onPick={(timetable) =>
                  jumpToWeekOf(new Date(`${timetable.effectiveFrom}T00:00:00`))
                }
                onCreate={() => setDatesDialog({})}
                onEditDates={(timetable) => setDatesDialog({ timetable })}
                onRemove={setRemovingTimetable}
                onPublish={setPublishingTimetable}
              />
            )}

            {weekError && (
              <QueryError title="Could not load this week" error={weekError} onRetry={refetchWeek} />
            )}

            {(isLoadingWeek || isLoadingTimetables) && (
              <Panel>
                <SkeletonLines count={6} />
              </Panel>
            )}

            {!isLoadingWeek && !isLoadingTimetables && !weekError && activePeriods.length === 0 && (
              <Panel>
                <EmptyState
                  icon={<CalendarDays className="h-8 w-8 text-indigo-600" />}
                  title="The day has no periods yet"
                  description="Set the bell times for your institute — period numbers, start and end times, and the breaks between them. Every class then shares the same day."
                  action={
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => setIsAddPeriodOpen(true)}
                      className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add the first period
                    </Button>
                  }
                />
              </Panel>
            )}

            {!isLoadingWeek &&
              !isLoadingTimetables &&
              !weekError &&
              sectionTimetables &&
              activePeriods.length > 0 &&
              !shownTimetable && (
                <Panel>
                  <EmptyState
                    icon={<CalendarDays className="h-8 w-8 text-indigo-600" />}
                    title={
                      sectionTimetables.timetables.length === 0
                        ? `${sectionTimetables.section.displayName} has no timetable yet`
                        : `No timetable applies to ${weekInfo.rangeLabel}`
                    }
                    description={
                      sectionTimetables.timetables.length === 0
                        ? 'Create one and choose the dates it applies between. Leave the end date blank to run it until the session ends.'
                        : "This section's timetables cover other dates. Create one for this week, or change an existing timetable's dates."
                    }
                    action={
                      canManageTimetable ? (
                        <Button
                          variant="primary"
                          size="md"
                          onClick={() => setDatesDialog({})}
                          className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white text-base"
                        >
                          <Plus className="h-4 w-4 " /> Create timetable
                        </Button>
                      ) : undefined
                    }
                  />
                </Panel>
              )}

            {!isLoadingWeek &&
              !isLoadingTimetables &&
              !weekError &&
              activePeriods.length > 0 &&
              shownTimetable && (
                <>
                  {isFiltered && (
                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 px-1 print:hidden">
                      {visibleEntryCount === 0
                        ? 'No lessons in this week match those filters.'
                        : `Showing ${visibleEntryCount} of ${entries.length} lessons.`}
                    </p>
                  )}

                  <TimetableWeekGrid
                    columns={columns}
                    periods={activePeriods}
                    entries={entries}
                    matchesFilter={matchesFilter}
                    isFiltered={isFiltered}
                    moving={moving}
                    isSavingMove={isSavingMove}
                    onStartMove={setMoving}
                    onCancelMove={() => setMoving(null)}
                    onDropInto={(day, period) => void moveTo(day, period)}
                    onPlace={handlePlace}
                    onEditEntry={setEditingEntry}
                    onRemoveEntry={setRemovingEntry}
                    onEditPeriod={setEditingPeriod}
                    onRemovePeriod={setRemovingPeriod}
                  />

                  <div className="print:hidden">
                    <TimetableLegend subjectNames={entries.map((entry) => entry.subject.name)} />
                  </div>
                </>
              )}
          </>
        )}

        {hasSection && isExamView && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
              <div>
                <h2 className="text-xl font-black text-slate-700 dark:text-white">
                  Examination timetable
                </h2>
                <p className="text-sm text-slate-500 font-medium">
                  Papers for {week?.section.displayName ?? 'this section'} in {weekInfo.rangeLabel}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsScheduleExamOpen(true)}
                className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 shrink-0 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                <Plus className="h-4 w-4" />
                <span>Schedule a paper</span>
              </button>
            </div>

            <div className="print:hidden">
              <Tabs
                options={[
                  { value: 'scheduled', label: 'Scheduled' },
                  {
                    value: 'past',
                    label: 'Past',
                    badge: pastExams.length > 0 ? pastExams.length : undefined,
                  },
                  {
                    value: 'deleted',
                    label: 'Deleted',
                    badge: sectionDeletedExams.length > 0 ? sectionDeletedExams.length : undefined,
                  },
                ]}
                value={examList}
                onChange={setExamList}
                label="Which papers to show"
              />
            </div>

            {examList === 'past' && (
              <div
                id={tabPanelId('past')}
                role="tabpanel"
                aria-labelledby="past-tab"
                className="flex flex-col gap-5"
              >
                {examsError && (
                  <QueryError title="Could not load the papers" error={examsError} onRetry={refetchExams} />
                )}

                {isLoadingExams && (
                  <Panel>
                    <SkeletonLines count={4} />
                  </Panel>
                )}

                {!isLoadingExams && !examsError && pastExams.length === 0 && (
                  <Panel>
                    <EmptyState
                      icon={<CalendarDays className="h-8 w-8 text-indigo-600" />}
                      title="No past papers"
                      description={`Papers ${week?.section.displayName ?? 'this section'} has already sat appear here, newest first.`}
                    />
                  </Panel>
                )}

                {!isLoadingExams && !examsError && pastExams.length > 0 && (
                  <ExamTimetableTable
                    exams={pastExams}
                    onEdit={setEditingExam}
                    onRemove={setRemovingExam}
                    onPublish={(exam) => void publishExam(exam)}
                    publishingUuid={publishingExam}
                  />
                )}
              </div>
            )}

            {examList === 'deleted' && (
              <div
                id={tabPanelId('deleted')}
                role="tabpanel"
                aria-labelledby="deleted-tab"
                className="flex flex-col gap-5"
              >
                {deletedExamsError && (
                  <QueryError
                    title="Could not load the deleted papers"
                    error={deletedExamsError}
                    onRetry={refetchDeletedExams}
                  />
                )}

                {isLoadingDeletedExams && (
                  <Panel>
                    <SkeletonLines count={4} />
                  </Panel>
                )}

                {!isLoadingDeletedExams && !deletedExamsError && sectionDeletedExams.length === 0 && (
                  <Panel>
                    <EmptyState
                      icon={<Trash2 className="h-8 w-8 text-indigo-600" />}
                      title="No deleted papers"
                      description={`Papers deleted from ${week?.section.displayName ?? 'this section'} appear here, with their marks kept, so they can be restored.`}
                    />
                  </Panel>
                )}

                {!isLoadingDeletedExams && !deletedExamsError && sectionDeletedExams.length > 0 && (
                  <ExamTimetableTable
                    variant="deleted"
                    exams={sectionDeletedExams}
                    onRestore={setRestoringExam}
                  />
                )}
              </div>
            )}

            {examList === 'scheduled' && (
              <div
                id={tabPanelId('scheduled')}
                role="tabpanel"
                aria-labelledby="scheduled-tab"
                className="flex flex-col gap-5"
              >
                {examsError && (
                  <QueryError title="Could not load the papers" error={examsError} onRetry={refetchExams} />
                )}

                {isLoadingExams && (
                  <Panel>
                    <SkeletonLines count={4} />
                  </Panel>
                )}

                {!isLoadingExams && !examsError && weekExams.length === 0 && (
                  <Panel>
                    <EmptyState
                      icon={<CalendarDays className="h-8 w-8 text-indigo-600" />}
                      title={`No papers in ${weekInfo.rangeLabel}`}
                      description={
                        nextExamElsewhere
                          ? `This section has ${sectionExams.length} ${sectionExams.length === 1 ? 'paper' : 'papers'} scheduled on other dates.`
                          : 'Schedule a paper to set its date, session, timings and marks.'
                      }
                      action={
                        nextExamElsewhere ? (
                          <Button
                            variant="secondary"
                            size="md"
                            className="mt-4"
                            onClick={() =>
                              jumpToWeekOf(new Date(`${nextExamElsewhere.examDate}T00:00:00`))
                            }
                          >
                            Go to the week of {formatDate(nextExamElsewhere.examDate)}
                          </Button>
                        ) : undefined
                      }
                    />
                  </Panel>
                )}

                {!isLoadingExams && !examsError && weekExams.length > 0 && (
                  <ExamTimetableTable
                    exams={weekExams}
                    onEdit={setEditingExam}
                    onRemove={setRemovingExam}
                    onPublish={(exam) => void publishExam(exam)}
                    publishingUuid={publishingExam}
                  />
                )}
              </div>
            )}
          </div>
        )}

  {selectedClass && !isExamView && (
          <section
            aria-labelledby="class-teaching-heading"
            className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 p-5 sm:p-6 print:hidden"
          >
            <h2 id="class-teaching-heading" className="text-base font-black text-slate-700 dark:text-white">
              {selectedClass.name} — class teacher and subject teachers
            </h2>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
              Each section has its own class teacher and its own subject teachers.
            </p>
            <ClassTeachingGrid
              classUuid={selectedClass.uuid}
              className={selectedClass.name}
              reloadKey={week}
            />
          </section>
        )}


      </div>

      {/* --- dialogs -------------------------------------------------------- */}
      {isCopyModalOpen && (
        <CopyTimetableModal
          classes={classes}
          source={{
            sectionUuid,
            sectionName:
              sectionTimetables?.section.displayName ??
              week?.section.displayName ??
              'This section',
            timetable: shownTimetable,
          }}
          onClose={() => setIsCopyModalOpen(false)}
          onSaved={refreshLessons}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteTimetableModal
          classes={classes}
          initialClassUuid={selectedClassUuid}
          initialSectionUuid={sectionUuid}
          initialTimetableUuid={shownTimetable?.uuid ?? null}
          onClose={() => setIsDeleteModalOpen(false)}
          onSaved={refreshLessons}
        />
      )}

      {isAddPeriodOpen && (
        <CreatePeriodModal
          existingPeriods={activePeriods}
          onClose={() => setIsAddPeriodOpen(false)}
          onSaved={() => {
            refetchPeriods();
            refetchWeek();
          }}
        />
      )}

      {placingSlot && week?.timetable && (
        <PlaceLessonModal
          timetableUuid={week.timetable.uuid}
          sectionName={week.section.displayName}
          day={placingSlot.day}
          period={placingSlot.period}
          subjects={week.allottableSubjects}
          classSubjects={sectionSubjects}
          teachers={options?.teachers ?? []}
          canAllot={canAllotTeachers}
          entries={entries}
          onClose={() => setPlacingSlot(null)}
          onSaved={refreshLessons}
        />
      )}

      {editingEntry && week && (
        <EditLessonModal
          entry={editingEntry}
          sectionName={week.section.displayName}
          subjects={week.allottableSubjects}
          entries={entries}
          onClose={() => setEditingEntry(null)}
          onSaved={refetchWeek}
        />
      )}

      {editingPeriod && (
        <PeriodTimingModal
          period={editingPeriod}
          onClose={() => setEditingPeriod(null)}
          onSaved={() => {
            refetchPeriods();
            refetchWeek();
          }}
        />
      )}

      {datesDialog && sectionTimetables && (
        <TimetableDatesModal
          sectionTimetables={sectionTimetables}
          timetable={datesDialog.timetable}
          holidays={holidays}
          defaultFrom={weekInfo.dates[0]}
          onClose={() => setDatesDialog(null)}
          onSaved={(saved) => {
            refreshLessons();
            // A new timetable is shown at once; moved dates keep the week in view.
            if (!datesDialog.timetable) {
              jumpToWeekOf(new Date(`${saved.effectiveFrom}T00:00:00`));
            }
          }}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(removingTimetable)}
        tone="danger"
        title={`Delete the timetable for ${removingTimetable ? describeTimetableRange(removingTimetable) : 'these dates'}?`}
        description={`Its ${removingTimetable?.lessons ?? 0} ${removingTimetable?.lessons === 1 ? 'lesson goes' : 'lessons go'} with it. Other timetables for this section are not touched.`}
        confirmLabel="Delete timetable"
        isLoading={isRemovingTimetable}
        onConfirm={() => void confirmRemoveTimetable()}
        onCancel={() => setRemovingTimetable(null)}
      />

      <ConfirmDialog
        // Tied to the lesson it was raised for, so a move abandoned with Escape
        // cannot bring it back on the next drag.
        isOpen={Boolean(repeatMove && moving && repeatMove.entryUuid === moving.uuid)}
        title={`Move ${moving ? capitalizeWords(moving.subject.name) : 'this lesson'} to ${repeatMove ? WEEKDAY_SHORT[repeatMove.day] : 'that day'}?`}
        description={REPEATED_SUBJECT_WARNING}
        confirmLabel="Move anyway"
        isLoading={isSavingMove}
        onConfirm={() => {
          if (repeatMove) void moveTo(repeatMove.day, repeatMove.period, true);
        }}
        onCancel={() => setRepeatMove(null)}
      />

      <ConfirmDialog
        isOpen={Boolean(publishingTimetable)}
        title={`Publish the timetable for ${publishingTimetable ? describeTimetableRange(publishingTimetable) : 'these dates'}?`}
        description={`Its ${publishingTimetable?.lessons ?? 0} ${publishingTimetable?.lessons === 1 ? 'lesson appears' : 'lessons appear'} in the teachers' days and on the student and parent timetables for these dates. It stays editable afterwards.`}
        confirmLabel="Publish timetable"
        isLoading={isPublishingTimetable}
        onConfirm={() => void confirmPublishTimetable()}
        onCancel={() => setPublishingTimetable(null)}
      />

      {/* One form for both: setting a paper and correcting one are the same fields. */}
      {(isScheduleExamOpen || editingExam) && (week || selectedSection) && (
        <ExamPaperModal
          sectionUuid={week?.section.uuid ?? selectedSection?.uuid ?? sectionUuid}
          sectionName={week?.section.displayName ?? selectedSection?.displayName ?? ''}
          subjects={examSubjects}
          exam={editingExam}
          defaultDate={weekInfo.dates[0]}
          holidays={holidays}
          onClose={() => {
            setIsScheduleExamOpen(false);
            setEditingExam(null);
          }}
          onSaved={refetchExams}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(examDayNotice)}
        title="This period is set for exams"
        description="An institute exam is scheduled in this period, so a regular lesson cannot be placed here. A paper without timings holds the whole day."
        confirmLabel="View exam timetable"
        cancelLabel="Close"
        onConfirm={viewExamTimetable}
        onCancel={() => setExamDayNotice(null)}
      >
        <div className="flex items-start gap-3 rounded-field border border-warn bg-warn-bg p-3.5">
          <span className="mt-0.5 shrink-0 rounded-lg bg-surface p-1.5 text-warn">
            <ClipboardList className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-ink">
              {examDayNotice
                ? new Date(`${examDayNotice}T00:00:00`).toLocaleDateString('en-IN', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : ''}
            </p>
            <p className="lbl mt-0.5">
              {week?.section.displayName ?? 'This section'} · {noticeExams.length}{' '}
              {noticeExams.length === 1 ? 'paper' : 'papers'}
            </p>
            <ul className="mt-2.5 flex flex-col gap-1.5">
              {noticeExams.map((exam) => (
                <li
                  key={exam.uuid}
                  className="flex items-center justify-between gap-3 rounded-lg bg-surface px-3 py-2 text-sm"
                >
                  <span className="truncate font-bold text-ink">
                    {capitalizeWords(exam.subject.name)}
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-ink-2">
                    <Clock className="h-3.5 w-3.5" aria-hidden />
                    {formatTimeRange(exam.startTime, exam.endTime, 'Time not set')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={Boolean(removingPeriod)}
        tone="danger"
        title={`Remove ${removingPeriod ? periodTitle(removingPeriod) : 'this period'}?`}
        description="This row is taken out of the day for every class. It is refused while any class still has a lesson in it."
        confirmLabel="Remove period"
        isLoading={isRemovingPeriod}
        onConfirm={() => void confirmRemovePeriod()}
        onCancel={() => setRemovingPeriod(null)}
      />

      <ConfirmDialog
        isOpen={Boolean(removingEntry)}
        tone="danger"
        title={`Delete ${removingEntry?.subject.name ? capitalizeWords(removingEntry.subject.name) : 'this lesson'}?`}
        description="It is taken out of this section's weekly timetable."
        confirmLabel="Delete lesson"
        isLoading={isRemovingEntry}
        onConfirm={() => void confirmRemoveEntry()}
        onCancel={() => setRemovingEntry(null)}
      />

      <ConfirmDialog
        isOpen={Boolean(removingExam)}
        tone="danger"
        title={`Remove ${removingExam?.name ?? 'this paper'}?`}
        description="It is taken off the examination timetable. Any marks already entered against it go with it."
        confirmLabel="Remove paper"
        isLoading={isRemovingExam}
        onConfirm={() => void confirmRemoveExam()}
        onCancel={() => setRemovingExam(null)}
      />

      <ConfirmDialog
        isOpen={Boolean(restoringExam)}
        title={`Restore ${restoringExam?.name ?? 'this paper'}?`}
        description={
          restoringExam
            ? `It goes back on the examination timetable on ${formatDate(restoringExam.examDate)}, with any marks entered before it was deleted.`
            : ''
        }
        confirmLabel="Restore paper"
        isLoading={isRestoringExam}
        onConfirm={() => void confirmRestoreExam()}
        onCancel={() => setRestoringExam(null)}
      />
    </div>
  );
}

/** The white card every state of this page — loading, empty, error — sits in. */
function Panel({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 p-8 sm:p-10">
      {children}
    </div>
  );
}
